import React, { useRef } from "react";
import MatchXp from "../../../shared/progress-ui/MatchXp";
import { flagUrlSquare } from "../../../shared/flags";
import { ordinal } from "../../../online/dynamic";
import WinnerCelebration, { celebrationFor } from "../match-over/WinnerCelebration";
import type { PublicPlayer } from "./types";
import s from "./RoyaleEnd.module.scss";

// The screens a Royale ends on for you (design B, picked in /debug/royale-end):
// one panel, you on the left (your flag with your celebration playing over it
// when you won, your place otherwise), what the Royale brought on the right,
// and the buttons along its foot: the way out on the left, the next Royale on
// the right.
//   win     you won: the right side is RoyaleReward (the win, its reward) and your XP
//   placed  the Royale is over and you placed (2nd, or wherever you watched on from)
//   out     you are out while it goes on: Keep watching too
//   watch   you only watched: the winner on the left, with their celebration
export type EndKind = "win" | "placed" | "out" | "watch";

export interface RoyaleEndProps {
  kind: EndKind;
  hero?: PublicPlayer;        // win: you; watch: the winner
  winner?: PublicPlayer;      // placed: who won
  place?: number | null;      // placed, out
  size: number;
  number: number;
  reward?: React.ReactNode;   // win: RoyaleReward
  onAgain?: () => void;
  onResults?: () => void;
  onLeave: () => void;
  onWatch?: () => void;       // out
}

function Hero({ player }: { player: PublicPlayer }) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const fx = celebrationFor(player);
  return (
    <>
      {fx && <WinnerCelebration player={player} anchorRef={ref} className={s.fx} />}
      <span ref={ref} className={s.ava}><img src={flagUrlSquare(player.flag || "xx")} alt="" /></span>
    </>
  );
}

export default function RoyaleEnd(p: RoyaleEndProps) {
  const win = p.kind === "win";
  const ord = p.place != null ? ordinal(p.place) : "";
  return (
    <div className={`${s.panel} ${win ? s.isWin : ""}`} data-xp-host="">
      <div className={`${s.left} ${win ? s.leftGold : ""}`}>
        {p.hero ? (<>
          <Hero player={p.hero} />
          <b className={s.name}>{p.hero.name}</b>
          <small className={s.tag}>{win ? "Royale winner" : "Won the Royale"}</small>
        </>) : (<>
          <b className={s.place}>{p.place}<sup>{ord.slice(String(p.place).length)}</sup></b>
          <small className={s.tag}>of {p.size}</small>
        </>)}
      </div>
      <div className={s.right}>
        {win ? p.reward : (<>
          <span className={`${s.kicker} ${p.kind === "out" ? s.kickerOut : ""}`}>{p.kind === "out" ? "You are out" : "The Royale is over"}</span>
          {p.kind === "placed" && p.winner && (
            <span className={s.chip}><img src={flagUrlSquare(p.winner.flag || "xx")} alt="" /><span><b>{p.winner.name}</b> won the Royale</span></span>
          )}
          {p.kind === "watch" && <span className={s.line}>Royale #{p.number}, {p.size} players</span>}
        </>)}
        {p.kind !== "watch" && <MatchXp className={s.xp} />}
      </div>
      <div className={s.foot}>
        <button type="button" className={s.btn} onClick={p.onLeave}>Back to start</button>
        {p.onResults && <button type="button" className={s.btn} onClick={p.onResults}>Results</button>}
        <span className={s.footR}>
          {p.onWatch && <button type="button" className={`${s.btn} ${s.btnWatch}`} onClick={p.onWatch}>Keep watching</button>}
          {p.onAgain && <button type="button" className={`${s.btn} ${s.btnGold}`} onClick={p.onAgain} data-sound="confirm">Join next Royale</button>}
        </span>
      </div>
    </div>
  );
}
